<template>
  <div class="max-w-4xl">
    <!-- Header Section -->
    <div class="card-modern bg-white rounded-lg shadow p-6 mb-6">
      <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between">
        <div class="mb-4 lg:mb-0">
          <div class="flex items-center gap-4 mb-2">
            <h1 class="text-2xl font-bold text-gray-900">
              {{ isEditMode ? 'Edit Expense' : 'Add New Expense' }}
            </h1>
          </div>
          <p class="text-gray-600">
            {{
              isEditMode
                ? 'Update expense information and details'
                : 'Create a new expense for vendor invoice tracking'
            }}
          </p>
        </div>
        <div class="flex items-center gap-3">
          <button
            @click="handleCancel"
            class="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <svg
              class="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Expenses
          </button>
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <div
      v-if="errors.general"
      class="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg"
    >
      <div class="flex">
        <div class="flex-shrink-0">
          <svg
            class="h-5 w-5 text-red-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm text-red-800">{{ errors.general }}</p>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="card-modern bg-white rounded-lg shadow p-6">
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <!-- Basic Information Section -->
        <div class="border-b border-gray-200 pb-6 mb-6">
          <h3 class="text-lg font-medium text-gray-900 mb-6">
            Basic Information
          </h3>

          <div class="space-y-6">
            <!-- Vendor Selection -->
            <div
              class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 items-start"
            >
              <label
                for="vendor_id"
                class="block text-sm font-medium text-gray-700 lg:pt-3"
              >
                Vendor <span class="text-red-500">*</span>
              </label>
              <div class="lg:col-span-2">
                <select
                  id="vendor_id"
                  v-model="form.vendor_id"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm"
                  :class="{
                    'border-red-300 focus:ring-red-500 focus:border-red-500':
                      errors.vendor_id,
                  }"
                  required
                >
                  <option value="">Select a vendor</option>
                  <option
                    v-for="vendor in vendors"
                    :key="vendor.id"
                    :value="vendor.id"
                  >
                    {{ vendor.name }}
                  </option>
                </select>
                <p v-if="errors.vendor_id" class="mt-2 text-sm text-red-600">
                  {{ errors.vendor_id }}
                </p>
              </div>
            </div>

            <!-- Invoice Number -->
            <div
              class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 items-start"
            >
              <label
                for="invoice_number"
                class="block text-sm font-medium text-gray-700 lg:pt-3"
              >
                Invoice Number <span class="text-red-500">*</span>
              </label>
              <div class="lg:col-span-2">
                <input
                  id="invoice_number"
                  v-model="form.invoice_number"
                  type="text"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm"
                  :class="{
                    'border-red-300 focus:ring-red-500 focus:border-red-500':
                      errors.invoice_number,
                  }"
                  placeholder="Enter invoice number"
                  required
                />
                <p
                  v-if="errors.invoice_number"
                  class="mt-2 text-sm text-red-600"
                >
                  {{ errors.invoice_number }}
                </p>
              </div>
            </div>

            <!-- Invoice Date -->
            <div
              class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 items-start"
            >
              <label
                for="invoice_date"
                class="block text-sm font-medium text-gray-700 lg:pt-3"
              >
                Invoice Date <span class="text-red-500">*</span>
              </label>
              <div class="lg:col-span-2">
                <input
                  id="invoice_date"
                  v-model="form.invoice_date"
                  type="date"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm"
                  :class="{
                    'border-red-300 focus:ring-red-500 focus:border-red-500':
                      errors.invoice_date,
                  }"
                  required
                />
                <p v-if="errors.invoice_date" class="mt-2 text-sm text-red-600">
                  {{ errors.invoice_date }}
                </p>
              </div>
            </div>

            <!-- Invoice Due Date -->
            <div
              class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 items-start"
            >
              <label
                for="invoice_due_date"
                class="block text-sm font-medium text-gray-700 lg:pt-3"
              >
                Invoice Due Date <span class="text-red-500">*</span>
              </label>
              <div class="lg:col-span-2">
                <input
                  id="invoice_due_date"
                  v-model="form.invoice_due_date"
                  type="date"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm"
                  :class="{
                    'border-red-300 focus:ring-red-500 focus:border-red-500':
                      errors.invoice_due_date,
                  }"
                  required
                />
                <p
                  v-if="errors.invoice_due_date"
                  class="mt-2 text-sm text-red-600"
                >
                  {{ errors.invoice_due_date }}
                </p>
              </div>
            </div>

            <!-- Invoice Amount -->
            <div
              class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 items-start"
            >
              <label
                for="invoice_amount"
                class="block text-sm font-medium text-gray-700 lg:pt-3"
              >
                Invoice Amount <span class="text-red-500">*</span>
              </label>
              <div class="lg:col-span-2">
                <div class="relative">
                  <div
                    class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                  >
                    <span class="text-gray-500 text-sm">$</span>
                  </div>
                  <input
                    id="invoice_amount"
                    v-model="form.invoice_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    class="w-full pl-6 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm"
                    :class="{
                      'border-red-300 focus:ring-red-500 focus:border-red-500':
                        errors.invoice_amount,
                    }"
                    placeholder="0.00"
                    required
                  />
                </div>
                <p
                  v-if="errors.invoice_amount"
                  class="mt-2 text-sm text-red-600"
                >
                  {{ errors.invoice_amount }}
                </p>
              </div>
            </div>

            <!-- Budget Category Field (Expense) -->
            <div
              class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 items-start"
            >
              <label
                for="budget_category_id"
                class="block text-sm font-medium text-gray-700 lg:pt-3"
              >
                Budget Category <span class="text-red-500">*</span>
              </label>
              <div class="lg:col-span-2">
                <select
                  id="budget_category_id"
                  v-model="form.budget_category_id"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm"
                  :class="{
                    'border-red-300 focus:ring-red-500 focus:border-red-500':
                      errors.budget_category_id,
                  }"
                  :disabled="isLoadingCategories"
                  required
                >
                  <option value="">Select a budget category</option>
                  <option
                    v-for="category in expenseCategories"
                    :key="category.id"
                    :value="category.id"
                  >
                    {{ category.name }}
                  </option>
                </select>
                <p
                  v-if="errors.budget_category_id"
                  class="mt-2 text-sm text-red-600"
                >
                  {{ errors.budget_category_id }}
                </p>
                <p v-else class="mt-2 text-sm text-gray-600">
                  Link this expense to a budget expense category for automatic
                  tracking
                </p>
                <button
                  type="button"
                  @click="showCategoryManager = true"
                  class="mt-2 inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary hover:text-primary-600 border border-primary rounded-lg hover:bg-primary-50 transition-colors duration-200"
                >
                  <svg
                    class="w-4 h-4 mr-1.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  Manage Budget Categories
                </button>
              </div>
            </div>

            <!-- Note -->
            <div
              class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 items-start"
            >
              <label
                for="note"
                class="block text-sm font-medium text-gray-700 lg:pt-3"
              >
                Note
              </label>
              <div class="lg:col-span-2">
                <textarea
                  id="note"
                  v-model="form.note"
                  rows="3"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm"
                  placeholder="Additional notes about this expense"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Payment Information Section -->
        <div class="border-b border-gray-200 pb-6 mb-6">
          <h3 class="text-lg font-medium text-gray-900 mb-6">
            Payment Information
          </h3>

          <div class="space-y-6">
            <!-- Status -->
            <div
              class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 items-start"
            >
              <label
                for="status"
                class="block text-sm font-medium text-gray-700 lg:pt-3"
              >
                Status <span class="text-red-500">*</span>
              </label>
              <div class="lg:col-span-2">
                <select
                  id="status"
                  v-model="form.status"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm"
                  :class="{
                    'border-red-300 focus:ring-red-500 focus:border-red-500':
                      errors.status,
                  }"
                  required
                >
                  <option value="">Select status</option>
                  <option
                    v-for="option in statusOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <p v-if="errors.status" class="mt-2 text-sm text-red-600">
                  {{ errors.status }}
                </p>
              </div>
            </div>

            <!-- Payment Method (shown when status is partial or paid) -->
            <div
              v-if="form.status === 'partial' || form.status === 'paid'"
              class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 items-start"
            >
              <label
                for="payment_method"
                class="block text-sm font-medium text-gray-700 lg:pt-3"
              >
                Payment Method <span class="text-red-500">*</span>
              </label>
              <div class="lg:col-span-2">
                <select
                  id="payment_method"
                  v-model="form.payment_method"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm"
                  :class="{
                    'border-red-300 focus:ring-red-500 focus:border-red-500':
                      errors.payment_method,
                  }"
                  required
                >
                  <option value="">Select payment method</option>
                  <option
                    v-for="option in paymentMethodOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <p
                  v-if="errors.payment_method"
                  class="mt-2 text-sm text-red-600"
                >
                  {{ errors.payment_method }}
                </p>
              </div>
            </div>

            <!-- Paid Amount (shown when status is partial) -->
            <div
              v-if="form.status === 'partial'"
              class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 items-start"
            >
              <label
                for="paid_amount"
                class="block text-sm font-medium text-gray-700 lg:pt-3"
              >
                Paid Amount <span class="text-red-500">*</span>
              </label>
              <div class="lg:col-span-2">
                <div class="relative">
                  <div
                    class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                  >
                    <span class="text-gray-500 text-sm">$</span>
                  </div>
                  <input
                    id="paid_amount"
                    v-model="form.paid_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    :max="form.invoice_amount"
                    class="w-full pl-6 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm"
                    :class="{
                      'border-red-300 focus:ring-red-500 focus:border-red-500':
                        errors.paid_amount,
                    }"
                    placeholder="0.00"
                    required
                  />
                </div>
                <p v-if="errors.paid_amount" class="mt-2 text-sm text-red-600">
                  {{ errors.paid_amount }}
                </p>
              </div>
            </div>

            <!-- Paid Date (shown when status is partial or paid) -->
            <div
              v-if="form.status === 'partial' || form.status === 'paid'"
              class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 items-start"
            >
              <label
                for="paid_date"
                class="block text-sm font-medium text-gray-700 lg:pt-3"
              >
                Paid Date <span class="text-red-500">*</span>
              </label>
              <div class="lg:col-span-2">
                <input
                  id="paid_date"
                  v-model="form.paid_date"
                  type="date"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm"
                  :class="{
                    'border-red-300 focus:ring-red-500 focus:border-red-500':
                      errors.paid_date,
                  }"
                  required
                />
                <p v-if="errors.paid_date" class="mt-2 text-sm text-red-600">
                  {{ errors.paid_date }}
                </p>
              </div>
            </div>

            <!-- Payment Details (shown when status is partial or paid) -->
            <div
              v-if="form.status === 'partial' || form.status === 'paid'"
              class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 items-start"
            >
              <label
                for="payment_details"
                class="block text-sm font-medium text-gray-700 lg:pt-3"
              >
                Payment Details
              </label>
              <div class="lg:col-span-2">
                <textarea
                  id="payment_details"
                  v-model="form.payment_details"
                  rows="3"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200 text-sm"
                  placeholder="Additional payment details (check number, transaction ID, etc.)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Document Upload Section -->
        <div class="mb-6">
          <h3 class="text-lg font-medium text-gray-900 mb-6">Documents</h3>

          <!-- File Upload -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Upload Documents
            </label>
            <div
              class="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors"
              :class="{ 'border-blue-400 bg-blue-50': isDragOver }"
              @dragover.prevent="handleDragOver"
              @dragleave.prevent="handleDragLeave"
              @drop.prevent="handleDrop"
            >
              <div class="space-y-1 text-center">
                <svg
                  class="mx-auto h-12 w-12 text-gray-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <div class="flex text-sm text-gray-600">
                  <label
                    for="file-upload"
                    class="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary"
                  >
                    <span>Upload files</span>
                    <input
                      id="file-upload"
                      ref="fileInput"
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                      class="sr-only"
                      @change="handleFileSelect"
                    />
                  </label>
                  <p class="pl-1">or drag and drop</p>
                </div>
                <p class="text-xs text-gray-500">
                  PDF, DOC, XLS, JPG, PNG up to 10MB each
                </p>
              </div>
            </div>
          </div>

          <!-- Existing Attachments (Edit Mode) -->
          <div
            v-if="
              isEditMode &&
              existingAttachments &&
              existingAttachments.length > 0
            "
            class="space-y-2 mb-4"
          >
            <h4 class="text-sm font-medium text-gray-700">
              Existing Documents
            </h4>
            <div
              v-for="attachment in existingAttachments"
              :key="attachment.id"
              class="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200"
            >
              <div class="flex items-center">
                <svg
                  class="h-5 w-5 text-blue-400 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <div>
                  <p class="text-sm font-medium text-gray-900">
                    {{ attachment.file_name }}
                  </p>
                  <p class="text-xs text-gray-500">
                    {{ formatFileSize(attachment.file_size) }} •
                    {{ formatDate(attachment.created_at) }}
                  </p>
                </div>
              </div>
              <div class="flex items-center space-x-2">
                <button
                  type="button"
                  @click="downloadExistingAttachment(attachment)"
                  class="text-blue-600 hover:text-blue-900"
                  title="Download"
                >
                  <svg
                    class="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Uploaded Files List -->
          <div v-if="uploadedFiles.length > 0" class="space-y-2">
            <h4 class="text-sm font-medium text-gray-700">
              New Files to Upload
            </h4>
            <div
              v-for="(file, index) in uploadedFiles"
              :key="index"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div class="flex items-center">
                <svg
                  class="h-5 w-5 text-gray-400 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <div>
                  <p class="text-sm font-medium text-gray-900">
                    {{ file.name }}
                  </p>
                  <p class="text-xs text-gray-500">
                    {{ formatFileSize(file.size) }}
                  </p>
                </div>
              </div>
              <button
                type="button"
                @click="removeFile(index)"
                class="text-red-600 hover:text-red-900"
              >
                <svg
                  class="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          <!-- Spacer for large screens to align with form fields -->
          <div class="hidden lg:block"></div>

          <!-- Buttons Container -->
          <div class="lg:col-span-2 flex flex-col sm:flex-row gap-4">
            <!-- Primary Button -->
            <button
              type="submit"
              :disabled="isSubmitting"
              class="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-primary hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <span v-if="isSubmitting" class="flex items-center">
                <svg
                  class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {{
                  isSubmitting
                    ? 'Saving...'
                    : isEditMode
                      ? 'Update Expense'
                      : 'Create Expense'
                }}
              </span>
              <span v-else>
                {{ isEditMode ? 'Update Expense' : 'Create Expense' }}
              </span>
            </button>

            <!-- Cancel Button -->
            <button
              type="button"
              @click="handleCancel"
              class="flex-1 flex justify-center py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>

    <!-- Expense Category Manager Modal -->
    <ExpenseCategoryManager
      :is-open="showCategoryManager"
      @close="handleCategoryManagerClose"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import {
  useExpense,
  useCreateExpense,
  useUpdateExpense,
  useUploadAttachment,
  useExpenseAttachments,
} from '../composables/useExpenses';
import { vendorsApi, queryKeys, expensesApi } from '@neibrpay/api-client';
import {
  getExpenseStatusOptions,
  getPaymentMethodOptions,
  type CreateExpenseDto,
  type UpdateExpenseDto,
  type ExpenseStatus,
  ExpenseStatus as ExpenseStatusEnum,
} from '@neibrpay/models';
import { useBudgetCategories } from '../composables/useBudget';
import ExpenseCategoryManager from '../components/ExpenseCategoryManager.vue';
import { useQueryClient } from '@tanstack/vue-query';

const router = useRouter();
const route = useRoute();
const queryClient = useQueryClient();

// Check if we're in edit mode
const isEditMode = computed(() => !!route.params.id);
const expenseId = computed(() =>
  isEditMode.value ? Number(route.params.id) : null
);

// Form data
const form = ref<CreateExpenseDto & { paid_amount?: number }>({
  vendor_id: 0,
  invoice_number: '',
  invoice_date: '',
  invoice_due_date: '',
  invoice_amount: 0,
  budget_category_id: null,
  note: '',
  status: ExpenseStatusEnum.UNPAID,
  payment_details: '',
  payment_method: undefined,
  paid_amount: 0,
  paid_date: '',
});

// Fetch expense budget categories
const { data: expenseCategoriesData, isLoading: isLoadingCategories } =
  useBudgetCategories('expense');
const expenseCategories = computed(() => expenseCategoriesData.value || []);

// File upload
const uploadedFiles = ref<File[]>([]);
const fileInput = ref<HTMLInputElement>();
const isDragOver = ref(false);

// Form state
const isSubmitting = ref(false);
const errors = ref<Record<string, string>>({});

// Category manager modal
const showCategoryManager = ref(false);

const handleCategoryManagerClose = () => {
  showCategoryManager.value = false;
  // Invalidate categories query to refresh the dropdown
  queryClient.invalidateQueries({
    queryKey: queryKeys.budget.categoryList('expense'),
  });
};

// Get vendors for dropdown
const { data: vendorsData } = useQuery({
  queryKey: queryKeys.vendors.list({}),
  queryFn: () => vendorsApi.list({}),
  select: (data: any) => data.data,
});

const vendors = computed(() => vendorsData.value || []);

// Get expense data if in edit mode
const { data: expenseData } = useExpense(expenseId.value || 0);

// Get existing attachments if in edit mode
const { data: existingAttachments } = useExpenseAttachments(
  expenseId.value || 0
);

// Options for dropdowns
const statusOptions = getExpenseStatusOptions();
const paymentMethodOptions = getPaymentMethodOptions();

// Mutations
const createExpenseMutation = useCreateExpense();
const updateExpenseMutation = useUpdateExpense();
const uploadAttachmentMutation = useUploadAttachment();

// Format date for HTML date input (YYYY-MM-DD)
const formatDateForInput = (dateString: string | null | undefined): string => {
  if (!dateString) return '';

  // If it's already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // Try to parse and format the date
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

// Load expense data in edit mode
watch(
  expenseData,
  (expense: any) => {
    if (expense && isEditMode.value) {
      form.value = {
        vendor_id: expense.vendor_id,
        invoice_number: expense.invoice_number,
        invoice_date: formatDateForInput(expense.invoice_date),
        invoice_due_date: formatDateForInput(expense.invoice_due_date),
        invoice_amount: expense.invoice_amount,
        budget_category_id: expense.budget_category_id || null,
        note: expense.note || '',
        status: expense.status,
        payment_details: expense.payment_details || '',
        payment_method: expense.payment_method,
        paid_amount: expense.paid_amount,
        paid_date: formatDateForInput(expense.paid_date) || '',
      };
    }
  },
  { immediate: true }
);

// Watch status changes to auto-fill paid amount
watch(
  () => form.value.status,
  (newStatus: ExpenseStatus) => {
    if (newStatus === ExpenseStatusEnum.PAID) {
      form.value.paid_amount = form.value.invoice_amount;
    } else if (newStatus === ExpenseStatusEnum.UNPAID) {
      form.value.paid_amount = 0;
      form.value.payment_method = undefined;
      form.value.paid_date = '';
      form.value.payment_details = '';
    }
  }
);

// File handling
const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files) {
    const newFiles = Array.from(target.files);
    uploadedFiles.value.push(...newFiles);
  }
  // Clear the input
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

const removeFile = (index: number) => {
  uploadedFiles.value.splice(index, 1);
};

// Drag and drop handlers
const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
  isDragOver.value = true;
};

const handleDragLeave = (event: DragEvent) => {
  event.preventDefault();
  // Only set isDragOver to false if we're leaving the drop zone entirely
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  const x = event.clientX;
  const y = event.clientY;

  if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
    isDragOver.value = false;
  }
};

const handleDrop = (event: DragEvent) => {
  event.preventDefault();
  isDragOver.value = false;

  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    const newFiles = Array.from(files);
    uploadedFiles.value.push(...newFiles);
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const downloadExistingAttachment = async (attachment: any) => {
  try {
    const blob = await expensesApi.downloadAttachment(
      expenseId.value!,
      attachment.id
    );
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = attachment.file_name;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error('Error downloading attachment:', error);
    alert('Failed to download file');
  }
};

// Form validation
const validateForm = (): boolean => {
  errors.value = {};

  if (!form.value.vendor_id) {
    errors.value.vendor_id = 'Vendor is required';
  }

  if (!form.value.invoice_number.trim()) {
    errors.value.invoice_number = 'Invoice number is required';
  }

  if (!form.value.invoice_date) {
    errors.value.invoice_date = 'Invoice date is required';
  }

  if (!form.value.invoice_due_date) {
    errors.value.invoice_due_date = 'Invoice due date is required';
  }

  if (
    form.value.invoice_due_date &&
    form.value.invoice_date &&
    new Date(form.value.invoice_due_date) < new Date(form.value.invoice_date)
  ) {
    errors.value.invoice_due_date = 'Due date must be after invoice date';
  }

  if (!form.value.invoice_amount || form.value.invoice_amount <= 0) {
    errors.value.invoice_amount = 'Invoice amount must be greater than 0';
  }

  if (!form.value.budget_category_id) {
    errors.value.budget_category_id = 'Budget category is required';
  }

  if (!form.value.status) {
    errors.value.status = 'Status is required';
  }

  if (form.value.status === 'partial') {
    if (!form.value.paid_amount || form.value.paid_amount <= 0) {
      errors.value.paid_amount = 'Paid amount is required for partial payments';
    } else if (form.value.paid_amount >= form.value.invoice_amount) {
      errors.value.paid_amount = 'Paid amount must be less than invoice amount';
    }
  }

  if (form.value.status === 'partial' || form.value.status === 'paid') {
    if (!form.value.payment_method) {
      errors.value.payment_method = 'Payment method is required';
    }
    if (!form.value.paid_date) {
      errors.value.paid_date = 'Paid date is required';
    }
  }

  return Object.keys(errors.value).length === 0;
};

// Form submission
const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  isSubmitting.value = true;
  errors.value = {};

  try {
    // Prepare form data
    const formData = { ...form.value };

    // Remove undefined values
    Object.keys(formData).forEach(key => {
      if (formData[key as keyof typeof formData] === undefined) {
        delete formData[key as keyof typeof formData];
      }
    });

    let expense;
    if (isEditMode.value) {
      expense = await updateExpenseMutation.mutateAsync({
        id: expenseId.value!,
        data: formData as UpdateExpenseDto,
      });
    } else {
      expense = await createExpenseMutation.mutateAsync(
        formData as CreateExpenseDto
      );
    }

    // Upload files if any
    if (uploadedFiles.value.length > 0 && expense) {
      for (const file of uploadedFiles.value) {
        try {
          await uploadAttachmentMutation.mutateAsync({
            expenseId: expense.id,
            file,
          });
        } catch (error) {
          console.error('Failed to upload file:', file.name, error);
          // Continue with other files even if one fails
        }
      }
    }

    // Redirect to expenses list
    router.push('/expenses');
  } catch (error: any) {
    console.error('Error saving expense:', error);
    errors.value.general = error.message || 'Failed to save expense';
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  router.push('/expenses');
};

// Set default dates
onMounted(() => {
  if (!isEditMode.value) {
    const today = new Date().toISOString().split('T')[0];
    form.value.invoice_date = today;
    form.value.invoice_due_date = today;
  }
});
</script>
